// Types and helpers for the timesheet (api/timesheets/*).

// What a row is booked as. The first three need a project.
export type TimesheetCategory = 'implementation' | 'acquisition' | 'backstopping' | 'other' | 'free_text';

export const CATEGORY_META: Record<TimesheetCategory, { label: string; short: string; color: string; project: boolean; disabled?: string }> = {
    implementation: { label: 'Project implementation', short: 'Implementation', color: '#2e7d32', project: true },
    acquisition: { label: 'Acquisition', short: 'Acquisition', color: '#d32f2f', project: true,
        disabled: 'Coming later - acquisition time needs access to the acquisition board.' },
    backstopping: { label: 'Backstopping', short: 'Backstopping', color: '#1565c0', project: true },
    other: { label: 'Others', short: 'Others', color: '#757575', project: false },
    free_text: { label: 'Free text', short: 'Free text', color: '#9e9e9e', project: false },
};
export const CATEGORY_ORDER: TimesheetCategory[] = ['implementation', 'acquisition', 'backstopping', 'other', 'free_text'];

export type TimesheetEntry = {
    id: number;
    date: string; // YYYY-MM-DD
    projectId: number | null;
    projectNumber: string | null;
    projectName: string | null;
    client: string | null;
    activity: string | null;
    rfsId: number | null;
    rfsName: string | null;
    rfsCode?: string | null;
    category: TimesheetCategory;
    otherCategoryId: number | null;
    otherCategoryCode: string | null;
    otherCategoryName: string | null;
    hours: number;
    description: string;
};

export type TimesheetRfsOption = { id: number; name: string; code: string | null };

// "Others" positions: 0100 General/Overarching, 0400 Sick Leave, 9999 Holidays, ...
export type TimesheetOtherCategory = { id: number; code: string | null; name: string };
export const otherLabel = (code: string | null, name: string | null) => `${code ? `${code} ` : ''}${name ?? ''}`.trim();

export type TimesheetProjectOption = {
    id: number;
    projectNumber: string | null;
    projectName: string | null;
    client: string | null;
    mine: boolean;
    // RfS tracking on: staff can pick an RfS (optional) or create a new one.
    rfsTracking: boolean;
    rfs: TimesheetRfsOption[];
};

// A grid row: a project (with category and optional RfS), an Others position or a free-text activity.
export type TimesheetRow = {
    key: string;
    category: TimesheetCategory;
    projectId: number | null;
    activity: string | null;
    rfsId: number | null;
    otherCategoryId: number | null;
    title: string;
    subtitle: string;
};

export type TimesheetReportRow = {
    userOid: string;
    userName: string;
    projectId: number | null;
    projectNumber: string | null;
    projectName: string | null;
    client: string | null;
    activity: string | null;
    rfsId: number | null;
    rfsName: string | null;
    rfsCode?: string | null;
    category: TimesheetCategory;
    otherCategoryId: number | null;
    otherCategoryCode: string | null;
    otherCategoryName: string | null;
    month: string; // YYYY-MM
    hours: number;
};

export const rowKey = (target: { category: TimesheetCategory; projectId: number | null; activity: string | null; rfsId: number | null; otherCategoryId: number | null }) =>
    target.projectId !== null ? `p:${target.projectId}:${target.category}${target.rfsId !== null ? `:r${target.rfsId}` : ''}`
        : target.otherCategoryId !== null ? `o:${target.otherCategoryId}`
        : `a:${(target.activity || '').trim().toLowerCase()}`;

// "RfS: Grid code review"
// "<code> · <name>" when the RfS has a code, else just the name.
export const rfsDisplay = (name: string | null, code?: string | null) => (code ? `${code} · ${name ?? ''}` : (name ?? ''));
export const rfsLabel = (name: string | null, code?: string | null) => `RfS: ${rfsDisplay(name, code)}`;

// RfS names are unique per project, ignoring case and extra spaces.
export const sameRfsName = (a: string, b: string) => a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

// Label of what a row/report line is booked on, e.g. "6066 NESP II · RfS: Workshop" or "0400 Sick Leave".
export const targetTitle = (t: {
    projectId: number | null; projectNumber: string | null; projectName: string | null; activity: string | null;
    otherCategoryCode?: string | null; otherCategoryName?: string | null;
}) => (t.projectId !== null ? `${t.projectNumber ?? ''} ${t.projectName ?? ''}`.trim()
    : t.otherCategoryName ? otherLabel(t.otherCategoryCode ?? null, t.otherCategoryName) : t.activity || '');

export const rowFromEntry = (entry: TimesheetEntry): TimesheetRow => ({
    key: rowKey(entry),
    category: entry.category,
    projectId: entry.projectId,
    activity: entry.activity,
    rfsId: entry.rfsId,
    otherCategoryId: entry.otherCategoryId,
    title: targetTitle(entry),
    subtitle: [CATEGORY_META[entry.category].short, entry.rfsId !== null ? rfsLabel(entry.rfsName, entry.rfsCode) : entry.projectId !== null ? entry.client : '']
        .filter(Boolean).join(' · '),
});

export const rowFromProject = (project: TimesheetProjectOption, category: TimesheetCategory, rfs: TimesheetRfsOption | null = null): TimesheetRow => {
    const target = { category, projectId: project.id, activity: null, rfsId: rfs?.id ?? null, otherCategoryId: null };
    return {
        ...target,
        key: rowKey(target),
        title: `${project.projectNumber ?? ''} ${project.projectName ?? ''}`.trim(),
        subtitle: [CATEGORY_META[category].short, rfs ? rfsLabel(rfs.name, rfs.code) : project.client].filter(Boolean).join(' · '),
    };
};

export const rowFromOther = (other: TimesheetOtherCategory): TimesheetRow => {
    const target = { category: 'other' as const, projectId: null, activity: null, rfsId: null, otherCategoryId: other.id };
    return { ...target, key: rowKey(target), title: otherLabel(other.code, other.name), subtitle: 'Others' };
};

export const rowFromActivity = (activity: string): TimesheetRow => {
    const target = { category: 'free_text' as const, projectId: null, activity: activity.trim(), rfsId: null, otherCategoryId: null };
    return { ...target, key: rowKey(target), title: activity.trim(), subtitle: 'Free text' };
};

// ---------- hours ----------

// Accepts "2", "2.5", "2,5", "2:30", "90m", "1h 30m"; rounds to the nearest quarter hour.
// Returns null for invalid input and 0 for an empty cell.
export const parseHours = (input: string): number | null => {
    const text = input.trim().toLowerCase().replace(',', '.');
    if (!text || text === '-') return 0;
    let hours: number;
    const colon = text.match(/^(\d{1,2}):(\d{1,2})$/);
    const hm = text.match(/^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m(?:in)?)?$/);
    if (colon) {
        hours = Number(colon[1]) + Number(colon[2]) / 60;
    } else if (/^\d+(\.\d+)?$/.test(text)) {
        hours = Number(text);
    } else if (hm && (hm[1] || hm[2])) {
        hours = Number(hm[1] || 0) + Number(hm[2] || 0) / 60;
    } else {
        return null;
    }
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) return null;
    // Anything above zero is at least a quarter hour, so a short entry never clears the cell.
    return hours > 0 ? Math.max(0.25, Math.round(hours * 4) / 4) : 0;
};

// 2.5 -> "2:30", 0 -> ""
export const formatHours = (hours: number, emptyForZero = true): string => {
    if (!hours && emptyForZero) return '';
    const total = Math.round(hours * 60);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// ---------- dates (calendar dates in UTC, no time zone shifts) ----------

const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
export const toIso = (date: Date) => date.toISOString().slice(0, 10);

export const addDays = (iso: string, days: number): string => {
    const d = toDate(iso);
    d.setUTCDate(d.getUTCDate() + days);
    return toIso(d);
};

export const todayIso = (): string => {
    const now = new Date();
    return toIso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};

export const mondayOf = (iso: string): string => {
    const d = toDate(iso);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return toIso(d);
};

export const weekDays = (weekStart: string, withWeekend: boolean): string[] =>
    Array.from({ length: withWeekend ? 7 : 5 }, (_, i) => addDays(weekStart, i));

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const dayLabel = (iso: string) => {
    const d = toDate(iso);
    return { weekday: DAY_NAMES[d.getUTCDay()], day: d.getUTCDate(), isWeekend: d.getUTCDay() === 0 || d.getUTCDay() === 6 };
};

export const longDate = (iso: string) => {
    const d = toDate(iso);
    return `${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getUTCDay()]}, ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// "This week · 21 – 25 Sep 2026"
export const weekLabel = (weekStart: string, withWeekend: boolean): string => {
    const current = mondayOf(todayIso());
    const prefix = weekStart === current ? 'This week' : weekStart === addDays(current, -7) ? 'Last week' : weekStart === addDays(current, 7) ? 'Next week' : '';
    const start = toDate(weekStart);
    const end = toDate(addDays(weekStart, withWeekend ? 6 : 4));
    const range = start.getUTCMonth() === end.getUTCMonth()
        ? `${start.getUTCDate()} – ${end.getUTCDate()} ${MONTHS[end.getUTCMonth()]} ${end.getUTCFullYear()}`
        : `${start.getUTCDate()} ${MONTHS[start.getUTCMonth()]} – ${end.getUTCDate()} ${MONTHS[end.getUTCMonth()]} ${end.getUTCFullYear()}`;
    return prefix ? `${prefix} · ${range}` : range;
};

export const monthLabel = (yyyyMm: string) => {
    const [y, m] = yyyyMm.split('-').map(Number);
    return `${MONTHS[m - 1]} ${y}`;
};

// Hint only - long days are highlighted, never blocked.
export const LONG_DAY_HOURS = 10;
